import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { STEPS, type StepId, stepIndex } from './wizardState';

/**
 * The sticky footer rail.
 *
 * `sticky` rather than `fixed`, so it sits at the bottom of the wizard's own
 * column instead of over whatever else the page is showing, and the content
 * above it scrolls the page normally. The primary action lives here, which is
 * the element the responsive pass (`edc85d2`) records losing to mobile browser
 * chrome once already — hence `pb-[env(safe-area-inset-bottom)]` and a real
 * background rather than a transparent bar.
 *
 * `Cancel` is drawn as an underlined text link and stays one: it leaves the
 * wizard, and giving a discard the same button weight as `Next` is how it gets
 * pressed by accident.
 */
export function WizardFooter({
  step,
  onCancel,
  onBack,
  onNext,
  nextLabel,
  blockedReason,
  busy,
}: {
  step: StepId;
  onCancel: () => void;
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  /** Why `Next` is unavailable — shown, not just hovered. */
  blockedReason?: string | null;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const index = stepIndex(step);

  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 -mx-1 mt-4 flex flex-wrap items-center gap-3 border-t',
        'bg-page px-1 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
      )}
    >
      <button
        type="button"
        onClick={onCancel}
        className="rounded text-sm text-ink-muted underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t('common.cancel')}
      </button>

      {blockedReason && (
        <p className="w-full text-xs text-red-600 dark:text-red-400 sm:w-auto">
          {blockedReason}
        </p>
      )}

      <div className="ms-auto flex items-center gap-3">
        <span className="text-sm text-ink-muted">
          {t('common.stepOfTotal', { step: index + 1, total: STEPS.length })}
        </span>
        {index > 0 && (
          <Button size="sm" variant="outline" onClick={onBack} disabled={busy}>
            <Icon as={ArrowLeft} aria-hidden />
            {t('common.back')}
          </Button>
        )}
        <Button size="sm" onClick={onNext} disabled={busy || Boolean(blockedReason)}>
          {nextLabel}
          {index < STEPS.length - 1 && <Icon as={ArrowRight} aria-hidden />}
        </Button>
      </div>
    </div>
  );
}
